export default function Privacy() {
	return (
		<div className="min-h-screen bg-vscode-bg">
			{/* Header */}
			<section className="bg-vscode-editor border-b border-vscode-border py-20">
				<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
					<h1 className="text-4xl font-semibold text-vscode-text-bright mb-4 font-sans">
						Privacy Policy
					</h1>
					<p className="text-lg text-vscode-text-muted font-mono">
						Last Updated: November 22, 2025
					</p>
				</div>
			</section>

			{/* Content */}
			<section className="py-12">
				<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="prose prose-invert max-w-none">
						<div className="space-y-8 text-vscode-text font-mono">
							<section>
								<h2 className="text-2xl font-semibold text-vscode-text-bright mb-4 font-sans">1. Information We Collect</h2>
								<p className="text-vscode-text-muted mb-4">
									SakaDev collects information to provide better services to our users. We collect information in the following ways:
								</p>
								
								<h3 className="text-xl font-semibold text-vscode-text-bright mb-3 font-sans mt-6">Information You Provide</h3>
								<ul className="list-disc list-inside space-y-2 text-vscode-text-muted ml-4">
									<li>Account information (email, username)</li>
									<li>Code and prompts you submit through the Service</li>
									<li>Feedback and support requests</li>
									<li>Payment information (processed through secure third-party providers)</li>
								</ul>

								<h3 className="text-xl font-semibold text-vscode-text-bright mb-3 font-sans mt-6">Automatically Collected Information</h3>
								<ul className="list-disc list-inside space-y-2 text-vscode-text-muted ml-4">
									<li>Usage data (features used, API calls made)</li>
									<li>Device information (OS version, IDE version)</li>
									<li>Error logs and performance data</li>
									<li>Anonymous telemetry data (can be disabled in settings)</li>
								</ul>
							</section>

							<section>
								<h2 className="text-2xl font-semibold text-vscode-text-bright mb-4 font-sans">2. How We Use Your Information</h2>
								<p className="text-vscode-text-muted mb-4">We use the collected information for:</p>
								<ul className="list-disc list-inside space-y-2 text-vscode-text-muted ml-4">
									<li>Providing and improving the Service</li>
									<li>Processing API requests and generating responses</li>
									<li>Billing and account management</li>
									<li>Debugging and fixing issues</li>
									<li>Analyzing usage patterns to improve features</li>
									<li>Communicating with you about updates and support</li>
									<li>Preventing fraud and abuse</li>
								</ul>
							</section>

							<section>
								<h2 className="text-2xl font-semibold text-vscode-text-bright mb-4 font-sans">3. Data Storage and Security</h2>
								<p className="text-vscode-text-muted mb-4">
									We implement appropriate technical and organizational measures to protect your data:
								</p>
								<ul className="list-disc list-inside space-y-2 text-vscode-text-muted ml-4">
									<li>Encryption in transit (TLS/SSL)</li>
									<li>Encryption at rest for sensitive data</li>
									<li>Regular security audits</li>
									<li>Access controls and authentication</li>
									<li>Secure cloud infrastructure</li>
								</ul>
								<p className="text-vscode-text-muted mt-4">
									Your code and prompts are processed temporarily and are not stored permanently unless necessary 
									for providing the Service (e.g., conversation history for context).
								</p>
							</section>

							<section>
								<h2 className="text-2xl font-semibold text-vscode-text-bright mb-4 font-sans">4. Data Sharing and Third Parties</h2>
								<p className="text-vscode-text-muted mb-4">
									We do not sell your personal data. We may share your information with:
								</p>
								<ul className="list-disc list-inside space-y-2 text-vscode-text-muted ml-4">
									<li><strong>AI Model Providers:</strong> Your prompts are sent to AI providers (Anthropic, OpenAI, etc.) to generate responses</li>
									<li><strong>Payment Processors:</strong> For handling transactions securely</li>
									<li><strong>Cloud Infrastructure:</strong> For hosting and delivering the Service</li>
									<li><strong>Analytics Services:</strong> For understanding usage patterns (anonymized)</li>
								</ul>
								<p className="text-vscode-text-muted mt-4">
									Third-party AI providers may have their own privacy policies. We encourage you to review them.
								</p>
							</section>

							<section>
								<h2 className="text-2xl font-semibold text-vscode-text-bright mb-4 font-sans">5. Your Rights and Choices</h2>
								<p className="text-vscode-text-muted mb-4">You have the right to:</p>
								<ul className="list-disc list-inside space-y-2 text-vscode-text-muted ml-4">
									<li>Access your personal data</li>
									<li>Correct inaccurate data</li>
									<li>Request deletion of your data</li>
									<li>Export your data</li>
									<li>Opt-out of telemetry collection</li>
									<li>Withdraw consent at any time</li>
								</ul>
								<p className="text-vscode-text-muted mt-4">
									To exercise these rights, contact us at <a href="mailto:privacy@sakadev.site" className="text-vscode-accent hover:text-vscode-text-bright">privacy@sakadev.site</a>
								</p>
							</section>

							<section>
								<h2 className="text-2xl font-semibold text-vscode-text-bright mb-4 font-sans">6. Cookies and Tracking</h2>
								<p className="text-vscode-text-muted mb-4">
									We use cookies and similar technologies for:
								</p>
								<ul className="list-disc list-inside space-y-2 text-vscode-text-muted ml-4">
									<li>Authentication and session management</li>
									<li>Remembering your preferences</li>
									<li>Analytics and performance monitoring</li>
								</ul>
								<p className="text-vscode-text-muted mt-4">
									You can control cookies through your browser settings. Note that disabling cookies may affect functionality.
								</p>
							</section>

							<section>
								<h2 className="text-2xl font-semibold text-vscode-text-bright mb-4 font-sans">7. Data Retention</h2>
								<p className="text-vscode-text-muted mb-4">
									We retain your data for as long as necessary to provide the Service and comply with legal obligations:
								</p>
								<ul className="list-disc list-inside space-y-2 text-vscode-text-muted ml-4">
									<li>Account information: Until account deletion</li>
									<li>Usage logs: 90 days</li>
									<li>Billing records: As required by law (typically 7 years)</li>
									<li>Conversation history: 30 days (can be cleared manually)</li>
								</ul>
							</section>

							<section>
								<h2 className="text-2xl font-semibold text-vscode-text-bright mb-4 font-sans">8. Children's Privacy</h2>
								<p className="text-vscode-text-muted mb-4">
									SakaDev is not intended for users under 13 years of age. We do not knowingly collect personal 
									information from children under 13. If you believe we have collected such information, please 
									contact us immediately.
								</p>
							</section>

							<section>
								<h2 className="text-2xl font-semibold text-vscode-text-bright mb-4 font-sans">9. International Data Transfers</h2>
								<p className="text-vscode-text-muted mb-4">
									Your data may be transferred to and processed in countries other than your own. We ensure 
									appropriate safeguards are in place to protect your data in accordance with this Privacy Policy.
								</p>
							</section>

							<section>
								<h2 className="text-2xl font-semibold text-vscode-text-bright mb-4 font-sans">10. Changes to This Policy</h2>
								<p className="text-vscode-text-muted mb-4">
									We may update this Privacy Policy from time to time. We will notify you of any changes by 
									posting the new Privacy Policy on this page and updating the "Last Updated" date. Continued 
									use of the Service after changes constitutes acceptance of the updated policy.
								</p>
							</section>

							<section>
								<h2 className="text-2xl font-semibold text-vscode-text-bright mb-4 font-sans">11. Contact Us</h2>
								<p className="text-vscode-text-muted mb-4">
									If you have questions or concerns about this Privacy Policy:
								</p>
								<ul className="list-none space-y-2 text-vscode-text-muted ml-4">
									<li>Email: <a href="mailto:privacy@sakadev.site" className="text-vscode-accent hover:text-vscode-text-bright">privacy@sakadev.site</a></li>
									<li>Support: <a href="mailto:support@sakadev.site" className="text-vscode-accent hover:text-vscode-text-bright">support@sakadev.site</a></li>
									<li>GitHub: <a href="https://github.com/rahmanazhar/SakaDev" target="_blank" rel="noopener noreferrer" className="text-vscode-accent hover:text-vscode-text-bright">github.com/rahmanazhar/SakaDev</a></li>
								</ul>
							</section>

							<section className="mt-12 pt-8 border-t border-vscode-border">
								<h2 className="text-2xl font-semibold text-vscode-text-bright mb-4 font-sans">Telemetry Settings</h2>
								<p className="text-vscode-text-muted mb-4">
									You can disable telemetry data collection at any time:
								</p>
								<ul className="list-disc list-inside space-y-2 text-vscode-text-muted ml-4">
									<li>VS Code: Open SakaDev Settings → General → Disable "Send Anonymous Usage Data"</li>
									<li>This will stop sending anonymous usage statistics while maintaining core functionality</li>
								</ul>
								<p className="text-vscode-text-muted mt-4">
									Learn more in our <a href="https://sakadev.site/more-info/telemetry" className="text-vscode-accent hover:text-vscode-text-bright">telemetry documentation</a>.
								</p>
							</section>
						</div>
					</div>
				</div>
			</section>
		</div>
	)
}
